import { AgendaTabs } from '../shell/AgendaTabs';
import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import type { ChurchEventSummary, EventOptions, MyEventTask } from '../../shared/api/types';
import { Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime, groupByDay } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { StatusBadge } from './shared';

function EventRow({ e }: { e: ChurchEventSummary }) {
  return (
    <li className="list__item agenda__item">
      <span className="agenda__time">{formatTime(e.startsAt)}</span>
      <span className="agenda__body">
        <Link to={`/app/eventos/${e.id}`}>
          <strong>{e.title}</strong>
        </Link>
        <span className="muted small">
          {e.ministry ? e.ministry.name : 'General'}
          {e.placeName ? ` · ${e.placeName}` : ''}
        </span>
      </span>
      {e.status === 'approved' ? null : <StatusBadge status={e.status} />}
    </li>
  );
}

/** Eventos de la iglesia: por aprobar, mis tareas y próximos. */
export function EventsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const [list, options] = await Promise.all([
        api.get<{ items: ChurchEventSummary[]; myTasks: MyEventTask[] }>(`/calendar/churches/${churchId}/church-events`, signal),
        api.get<EventOptions>(`/calendar/churches/${churchId}/church-events/options`, signal),
      ]);
      return { ...list, options };
    },
    [churchId],
  );

  if (!churchId) {
    return (
      <div className="page">
        <h1>Eventos</h1>
        <EmptyState title="Elige primero una iglesia en el selector de arriba." />
      </div>
    );
  }

  const canCreate = data.status === 'ready' && data.data && (data.data.options.canCreateGeneral || data.data.options.ministries.length > 0);
  const items = data.status === 'ready' && data.data ? data.data.items : [];
  const toDecide = items.filter((e) => e.canDecide || e.pendingInvitations.length > 0);
  const upcoming = items.filter((e) => e.status !== 'rejected' && e.status !== 'canceled');
  const mine = items.filter((e) => e.status === 'rejected');
  const tasks = data.status === 'ready' && data.data ? data.data.myTasks.filter((t) => !t.done) : [];

  return (
    <div className="page">
      <AgendaTabs />
      <header className="page__header page__header--actions">
        <div>
          <h1>Eventos</h1>
          <p className="muted">{me.activeContext?.churchName}</p>
        </div>
        {canCreate ? (
          <Link className="btn btn--primary" to="/app/eventos/nuevo">
            Nuevo evento
          </Link>
        ) : null}
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data ? (
        <>
          {toDecide.length > 0 ? (
            <Card title={`Esperan tu respuesta (${toDecide.length})`}>
              <ul className="list">
                {toDecide.map((e) => (
                  <li key={e.id} className="list__item">
                    <span className="agenda__body">
                      <Link to={`/app/eventos/${e.id}`}>
                        <strong>{e.title}</strong>
                      </Link>
                      <span className="muted small">
                        {e.canDecide ? (e.scope === 'general' ? 'Evento general por aprobar' : `Evento de ${e.ministry?.name} por autorizar`) : `Invitan a ${e.pendingInvitations.map((m) => m.name).join(', ')}`}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          {tasks.length > 0 ? (
            <Card title="Mis tareas">
              <ul className="list">
                {tasks.map((t) => (
                  <li key={t.taskId} className="list__item">
                    <span className="agenda__body">
                      <strong>{t.title}</strong>
                      <Link className="small" to={`/app/eventos/${t.eventId}`}>
                        {t.eventTitle} · {new Date(t.startsAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          {upcoming.length === 0 ? (
            <Card>
              <EmptyState title="No hay eventos próximos.">{canCreate ? <p className="muted small">Crea el primero con «Nuevo evento».</p> : null}</EmptyState>
            </Card>
          ) : (
            <div className="agenda">
              {groupByDay(upcoming).map((day) => (
                <section key={day.key} className="agenda__day" aria-label={formatDayHeading(day.date)}>
                  <h2 className="agenda__date">{formatDayHeading(day.date)}</h2>
                  <ul className="list">
                    {day.items.map((e) => (
                      <EventRow key={e.id} e={e} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
          {mine.length > 0 ? (
            <Card title="No aprobados">
              <ul className="list">
                {mine.map((e) => (
                  <EventRow key={e.id} e={e} />
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
